import React from 'react';
import {
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Edit,
  Check,
  Close,
  Refresh,
  StarBorder,
  Star,
  ViewModule,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dashboardService } from '../services/dashboardService';
import { Dashboard, DashboardDetail, PlacedWidget, WidgetDescriptor } from './types';
import WidgetGrid from './WidgetGrid';
import WidgetRenderer from './WidgetRenderer';
import AddWidgetDialog from './AddWidgetDialog';
import InsightStrip from './InsightStrip';
import { useRefreshAllWidgets } from './useWidgetData';
import { useAuth } from '../context/AuthContext';

/**
 * The dashboard.
 *
 * Edit mode is explicit rather than always-on: an ERP dashboard is a reading
 * surface most of the time, and a grid that rearranges when you meant to click
 * a chart is the fastest way to make people distrust it. Layout changes are
 * held locally and saved in one atomic call, so abandoning an edit leaves the
 * stored board untouched.
 */

/** Local-time greeting. Uses the viewer clock, not the server's. */
const greeting = (): string => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const DashboardPage: React.FC = () => {
  const qc = useQueryClient();
  const { user } = useAuth();
  // Split on whitespace, not the letter "s" — /s+/ would turn "Asha" into "A".
  const firstName = (user?.name || '').trim().split(/\s+/)[0] || '';
  const refreshAll = useRefreshAllWidgets();

  const [selectedId, setSelectedId] = React.useState<number | null>(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState<PlacedWidget[] | null>(null);
  const [addOpen, setAddOpen] = React.useState(false);
  const [toast, setToast] = React.useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const { data: list, isLoading: listLoading } = useQuery<{ dashboards: Dashboard[]; count: number }>({
    queryKey: ['dashboards'],
    queryFn: () => dashboardService.list(),
  });

  // Memoised for the same reason: a new [] each render re-fires the effect below.
  const dashboards: Dashboard[] = React.useMemo(() => list?.dashboards ?? [], [list]);

  // Land on the user's default, else the first available.
  React.useEffect(() => {
    if (selectedId === null && dashboards.length > 0) {
      setSelectedId((dashboards.find((d) => d.isDefault) ?? dashboards[0]).id);
    }
  }, [dashboards, selectedId]);

  const { data: detail, isLoading: detailLoading } = useQuery<DashboardDetail>({
    queryKey: ['dashboard', selectedId],
    queryFn: () => dashboardService.get(selectedId as number),
    enabled: selectedId !== null,
  });

  // The board being displayed: the local draft while editing, else the server's.
  const widgets: PlacedWidget[] = draft ?? detail?.widgets ?? [];

  const saveLayout = useMutation({
    mutationFn: (next: PlacedWidget[]) =>
      dashboardService.saveLayout(selectedId as number, {
        widgets: next.map((w) => ({
          widgetKey: w.widgetKey,
          widgetVersion: w.widgetVersion,
          gridX: w.gridX,
          gridY: w.gridY,
          gridW: w.gridW,
          gridH: w.gridH,
          config: w.config ?? null,
          isCollapsed: w.isCollapsed ?? false,
          isPinned: w.isPinned ?? false,
        })),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard', selectedId] });
      setDraft(null);
      setIsEditing(false);
      setToast({ msg: 'Layout saved', severity: 'success' });
    },
    onError: (e: any) => setToast({ msg: e?.message || 'Could not save layout', severity: 'error' }),
  });

  const setDefault = useMutation({
    mutationFn: () => dashboardService.setDefault(selectedId as number),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboards'] });
      setToast({ msg: 'Set as your default dashboard', severity: 'success' });
    },
  });

  const startEditing = () => {
    setDraft(detail?.widgets ? [...detail.widgets] : []);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    // Discard, do not save. The stored layout is whatever it was.
    setDraft(null);
    setIsEditing(false);
  };

  const handleAdd = (descriptor: WidgetDescriptor) => {
    const base: PlacedWidget[] = draft ?? detail?.widgets ?? [];
    // Drop it below everything so it never lands on top of an existing tile.
    const lowest = base.reduce((max: number, w: PlacedWidget) => Math.max(max, w.gridY + w.gridH), 0);
    const placed: PlacedWidget = {
      // Negative temporary id — the server assigns the real one on save, and a
      // negative value can never collide with a persisted row.
      id: -Date.now(),
      widgetKey: descriptor.key,
      widgetVersion: descriptor.version,
      gridX: 0,
      gridY: lowest,
      gridW: descriptor.defaultSize.w,
      gridH: descriptor.defaultSize.h,
      config: {},
      descriptor,
    };
    setDraft([...base, placed]);
    setIsEditing(true);
  };

  const handleRemove = (id: number) => {
    const base: PlacedWidget[] = draft ?? detail?.widgets ?? [];
    setDraft(base.filter((w) => w.id !== id));
  };

  /** Remove every placement of a key — the picker's toggle-off path. */
  const handleRemoveByKey = (widgetKey: string) => {
    const base: PlacedWidget[] = draft ?? detail?.widgets ?? [];
    setDraft(base.filter((w) => w.widgetKey !== widgetKey));
    setIsEditing(true);
  };

  /**
   * Re-flow the board into full rows, left to right, preserving order.
   *
   * After a few add/remove/resize cycles a grid drifts into ragged columns
   * even with vertical compaction, because compaction only pulls tiles up —
   * it never closes a horizontal gap. This packs them.
   */
  const handleTidy = () => {
    const base: PlacedWidget[] = draft ?? detail?.widgets ?? [];
    const COLS = 12;
    let x = 0;
    let y = 0;
    let rowHeight = 0;

    const packed = [...base]
      .sort((a, b) => a.gridY - b.gridY || a.gridX - b.gridX)
      .map((w) => {
        const width = Math.min(w.gridW, COLS);
        if (x + width > COLS) {
          x = 0;
          y += rowHeight;
          rowHeight = 0;
        }
        const placed = { ...w, gridX: x, gridY: y };
        x += width;
        rowHeight = Math.max(rowHeight, w.gridH);
        return placed;
      });

    setDraft(packed);
    setIsEditing(true);
  };

  const current = dashboards.find((d) => d.id === selectedId);
  const busy = listLoading || detailLoading;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          mb: 2.5,
        }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 220 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
              {greeting()}
              {firstName ? `, ${firstName}` : ''}
            </Typography>
            <Box component="span" sx={{ fontSize: '1.5rem', lineHeight: 1 }} aria-hidden="true">
              👋
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
            <Typography variant="body2" color="text.secondary">
              Here's what's happening in your workspace today.
            </Typography>

            {/* The board switcher moves out of the title and becomes a quiet
                control — the heading should greet the person, not name a
                database row. */}
            {dashboards.length > 1 && (
              <Select
                value={selectedId ?? ''}
                onChange={(e) => {
                  setDraft(null);
                  setIsEditing(false);
                  setSelectedId(Number(e.target.value));
                }}
                size="small"
                variant="standard"
                disableUnderline
                sx={{
                  fontSize: '0.8125rem',
                  color: 'text.secondary',
                  '& .MuiSelect-select': { py: 0, pr: '20px !important' },
                }}
              >
                {dashboards.map((d) => (
                  <MenuItem key={d.id} value={d.id} sx={{ fontSize: '0.8125rem' }}>
                    {d.name}
                    {d.isDefault ? ' ★' : ''}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>
        </Box>

        {!isEditing && (
          <>
            <Tooltip title="Refresh all widgets">
              <IconButton size="small" onClick={() => refreshAll()}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
            {current && !current.isDefault && (
              <Tooltip title="Make this my default">
                <IconButton size="small" onClick={() => setDefault.mutate()}>
                  <StarBorder fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {current?.isDefault && (
              <Tooltip title="This is your default dashboard">
                <Star fontSize="small" sx={{ color: 'warning.main' }} />
              </Tooltip>
            )}
            <Button size="small" startIcon={<Edit />} onClick={startEditing} disabled={!selectedId}>
              Customise
            </Button>
          </>
        )}

        {isEditing && (
          <>
            <Button size="small" startIcon={<Add />} onClick={() => setAddOpen(true)}>
              Add widget
            </Button>
            <Tooltip title="Re-flow tiles into tidy rows">
              <Button size="small" startIcon={<ViewModule />} onClick={handleTidy}>
                Tidy up
              </Button>
            </Tooltip>
            <Button size="small" startIcon={<Close />} onClick={cancelEditing} color="inherit">
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={saveLayout.isPending ? <CircularProgress size={14} color="inherit" /> : <Check />}
              onClick={() => saveLayout.mutate(widgets)}
              disabled={saveLayout.isPending}
            >
              Save layout
            </Button>
          </>
        )}
      </Box>

      {/* Attention first: insights are rule-computed server-side and work
          whether or not AI credentials are configured. */}
      {!isEditing && <InsightStrip />}

      {busy && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!busy && widgets.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            This dashboard is empty
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add the widgets that matter to your role.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setDraft(detail?.widgets ? [...detail.widgets] : []);
              setIsEditing(true);
              setAddOpen(true);
            }}
          >
            Add your first widget
          </Button>
        </Box>
      )}

      {!busy && widgets.length > 0 && (
        <WidgetGrid
          widgets={widgets}
          isEditing={isEditing}
          compact={current?.layoutMode === 'COMPACT'}
          onLayoutChange={setDraft}
          renderWidget={(w) => (
            <Box sx={{ height: '100%', position: 'relative' }}>
              <WidgetRenderer widget={w} isEditing={isEditing} />
              {isEditing && (
                <Tooltip title="Remove">
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(w.id)}
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      zIndex: 2,
                      backgroundColor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      '&:hover': { backgroundColor: 'error.main', color: '#fff' },
                    }}
                  >
                    <Close sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        />
      )}

      <AddWidgetDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={handleAdd}
        onRemove={handleRemoveByKey}
        existingKeys={widgets.map((w) => w.widgetKey)}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity} variant="filled" onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DashboardPage;
